const AUTH_SESSION_PRESENCE_COOKIE = 'palenAuthSession';
const AUTH_SESSION_PRESENCE_STORAGE_KEY = 'palenAuthSession';
const AUTH_SESSION_PRESENCE_VALUE = '1';
const EXPLICIT_LOGOUT_PREFIX = 'logout:';
const AUTHENTICATED_BOOT_CLASS = 'palen-auth-boot';

export class AuthSessionPresenceService {
  hasActiveSessionHint(): boolean {
    return this.hasPresenceCookie() || this.hasPresenceStorage();
  }

  isAuthenticatedSessionPresenceEvent(event: StorageEvent): boolean {
    return this.isSessionPresenceStorageEvent(event) && event.newValue === AUTH_SESSION_PRESENCE_VALUE;
  }

  isUnauthenticatedSessionPresenceEvent(event: StorageEvent): boolean {
    return this.isSessionPresenceStorageEvent(event) && (event.newValue === null || this.isExplicitLogoutEvent(event));
  }

  isExplicitLogoutEvent(event: StorageEvent): boolean {
    return this.isSessionPresenceStorageEvent(event) && Boolean(event.newValue?.startsWith(EXPLICIT_LOGOUT_PREFIX));
  }

  markAuthenticated(): void {
    try {
      localStorage.setItem(AUTH_SESSION_PRESENCE_STORAGE_KEY, AUTH_SESSION_PRESENCE_VALUE);
    } catch {
      // Ignore storage errors; the server cookie remains the source of truth.
    }
  }

  markUnauthenticated({ explicitLogout = false }: { explicitLogout?: boolean } = {}): void {
    this.clearPresenceCookie();

    try {
      if (explicitLogout) localStorage.setItem(AUTH_SESSION_PRESENCE_STORAGE_KEY, `${EXPLICIT_LOGOUT_PREFIX}${crypto.randomUUID()}`);
      else localStorage.removeItem(AUTH_SESSION_PRESENCE_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }

  /**
   * Removes prerendered public markup before React paints the authenticated boot.
   */
  prepareAuthenticatedBoot(rootElement: HTMLElement): boolean {
    if (!this.hasAuthenticatedBootClass()) {
      return false;
    }

    rootElement.replaceChildren();
    return true;
  }

  finishAuthenticatedBoot(): void {
    document.documentElement.classList.remove(AUTHENTICATED_BOOT_CLASS);
  }

  private hasPresenceCookie(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    return document.cookie.split(';').some((part) => {
      return part.trim() === `${AUTH_SESSION_PRESENCE_COOKIE}=${AUTH_SESSION_PRESENCE_VALUE}`;
    });
  }

  private clearPresenceCookie(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.cookie = `${AUTH_SESSION_PRESENCE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
  }

  private hasPresenceStorage(): boolean {
    try {
      return localStorage.getItem(AUTH_SESSION_PRESENCE_STORAGE_KEY) === AUTH_SESSION_PRESENCE_VALUE;
    } catch {
      return false;
    }
  }

  private isSessionPresenceStorageEvent(event: StorageEvent): boolean {
    return event.key === AUTH_SESSION_PRESENCE_STORAGE_KEY;
  }

  private hasAuthenticatedBootClass(): boolean {
    return document.documentElement.classList.contains(AUTHENTICATED_BOOT_CLASS);
  }
}

export const authSessionPresenceService = new AuthSessionPresenceService();
